import React, {useEffect, useState, useMemo} from "react"
import {useParams} from "react-router-dom";
import BreadCrumb from "../../../Common/BreadCrumb";
import {useTranslation} from "react-i18next";
import {Form, Container, Card, Row, Col} from "react-bootstrap";
import {mockScenarios} from "../../../data/scenarios";
import {mockRules} from "../../../data/rules";
import ConditionGroupRenderer from "../../Components/ConditionGroupRenderer";
import {dashboardAPI} from "../../../services/api";

const RuleDetails = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const [rule, setRule] = useState<Rule | null>(null);
    const [scenario, setScenario] = useState<Scenario | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        if (!id) return;

        if (isDemoUser) {
            // Use mock data for demo users
            const foundRule = mockRules.find((r) => r.id === id);
            if (foundRule) {
                const foundScenario = mockScenarios.find((s) => s.id === foundRule.scenarioId);
                setRule(foundRule);
                setScenario(foundScenario || null);
            }
            return;
        }

        // Fetch rule from backend for real users
        const fetchRule = async () => {
            setIsLoading(true);
            try {
                console.log('📋 Fetching rule from backend:', id);
                const response: any = await dashboardAPI.getRuleById(id);
                
                // Normalize response - handle both AxiosResponse and direct data
                const ruleData = response?.data || response;
                
                if (!ruleData) {
                    console.warn('⚠️ Rule not found:', id);
                    setRule(null);
                    return;
                }
                
                console.log('✅ Rule received:', ruleData);
                
                // Parse conditionTreeJson to extract conditionGroup
                let conditionGroup: ConditionGroup = { join: 'and', conditions: [] };
                if (ruleData.conditionTreeJson) {
                    try {
                        conditionGroup = treeJsonToConditionGroup(ruleData.conditionTreeJson);
                    } catch (e) {
                        console.warn('⚠️ Failed to parse conditionTreeJson:', e);
                    }
                }
                
                // Format date (backend returns LocalDateTime, convert to string)
                const createdOn = ruleData.createdOn 
                    ? (typeof ruleData.createdOn === 'string' 
                        ? ruleData.createdOn.split('T')[0] 
                        : new Date(ruleData.createdOn).toISOString().split('T')[0])
                    : new Date().toISOString().split('T')[0];
                
                const mappedRule: Rule = {
                    id: ruleData.id || id,
                    name: ruleData.name || '',
                    active: ruleData.active !== false,
                    scenarioId: ruleData.scenarioId || '',
                    createdOn: createdOn,
                    description: ruleData.description || '',
                    alertCount: 0, // Backend doesn't provide this
                    conditionGroup: conditionGroup
                };
                
                setRule(mappedRule);
                
                // Fetch scenario if scenarioId is available
                if (ruleData.scenarioId) {
                    try {
                        const scenarioResponse: any = await dashboardAPI.getScenarioById(ruleData.scenarioId);
                        const scenarioData = scenarioResponse?.data || scenarioResponse;
                        if (scenarioData) {
                            setScenario({
                                id: scenarioData.id || '',
                                name: scenarioData.name || '',
                                active: scenarioData.active !== false,
                                createdOn: scenarioData.createdOn 
                                    ? (typeof scenarioData.createdOn === 'string' 
                                        ? scenarioData.createdOn.split('T')[0] 
                                        : new Date(scenarioData.createdOn).toISOString().split('T')[0])
                                    : '',
                                description: scenarioData.description || '',
                                conditions: []
                            });
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to fetch scenario:', error);
                    }
                }
            } catch (error: any) {
                console.error('❌ Failed to fetch rule:', error);
                setRule(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRule();
    }, [id, isDemoUser]);

    // Helper function to convert backend conditionTreeJson to frontend conditionGroup
    const treeJsonToConditionGroup = (treeJson: string): ConditionGroup => {
        if (!treeJson) {
            return { join: 'and', conditions: [] };
        }

        try {
            const tree = JSON.parse(treeJson);
            const convertNode = (node: any): any => {
                if (node.type === 'CONDITION' && node.condition) {
                    return {
                        field: node.condition.field,
                        operator: node.condition.operator,
                        value: node.condition.value
                    };
                }

                if (node.conditions && Array.isArray(node.conditions)) {
                    const join = node.type === 'OR' ? 'or' : 'and';
                    return {
                        join: join,
                        conditions: node.conditions.map((child: any) => convertNode(child))
                    };
                }

                return { join: 'and', conditions: [] };
            };

            return convertNode(tree);
        } catch (e) {
            console.error('Failed to parse conditionTreeJson:', e);
            return { join: 'and', conditions: [] };
        }
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header'), path: "/"},
                            {label: t('RuleList.page_title'), path: "/rules"},
                            {label: id as string, path: "/rules"},
                        ]}
                    />

                    <Card>
                        <Card.Body>
                            <h4 className="mb-4">{t('RuleList.rule_details')}</h4>
                            {isLoading && <div>Loading...</div>}
                            {!isLoading && !rule && <div>Rule not found</div>}
                            {
                                !isLoading && rule && <Form>
                                    <Row className="mb-3">
                                        <Form.Group as={Col} md="6" controlId="scenarioId">
                                            <Form.Label>{t('RuleList.rule_id')}</Form.Label>
                                            <Form.Control type="text" value={rule.id} disabled/>
                                        </Form.Group>

                                        <Form.Group as={Col} md="6" controlId="createdOn">
                                            <Form.Label>{t('RuleList.created_on')}</Form.Label>
                                            <Form.Control type="text" value={rule.createdOn} disabled/>
                                        </Form.Group>
                                    </Row>

                                    <Form.Group className="mb-3" controlId="scenarioName">
                                        <Form.Label>{t('RuleList.rule_name')}</Form.Label>
                                        <Form.Control type="text" value={rule.name} readOnly/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="description">
                                        <Form.Label>{t('RuleList.rule_description')}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={rule.description}
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="description">
                                        <Form.Label>{t('RuleList.scenario_name')}</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={scenario?.name}
                                        />
                                    </Form.Group>

                                    <Form.Group controlId="activeStatus" className="mb-3">
                                        <Form.Label>{t('RuleList.active')}</Form.Label>
                                        <div>
                                            <Form.Check
                                                type="checkbox"
                                                role="switch"
                                                id="SwitchCheck9"
                                                checked={rule.active}/>
                                        </div>
                                    </Form.Group>
                                    <Form.Label>{t('RuleList.rule_conditions')}</Form.Label>
                                    <ConditionGroupRenderer group={rule.conditionGroup} t={t}/>
                                </Form>
                            }

                        </Card.Body>
                    </Card>
                </Container>
            </div>

        </React.Fragment>
    )

}
export default RuleDetails;